import React, { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Form, Field } from 'react-final-form';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Box,
  CircularProgress,
  Unstable_Grid2 as Grid
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import TextFieldAdapter from 'components/shared/field-adapters/TextFieldAdapter';
import PrimaryLoadingButton from 'components/shared/buttons/PrimaryLoadingButton';
import StyledAppbarButtonLink from 'common/theme/appbar/StyledAppbarButtonLink';
import requestStatus from 'common/constants/status';
import sessionTypeSlice from 'store/modules/sessionType';

const validate = (values) => {
  const errors = {};

  if (!values.name_es || values.name_es.trim().length < 2) {
    errors.name_es = 'Debe tener al menos 2 caracteres';
  }

  if (!values.name_en || values.name_en.trim().length < 2) {
    errors.name_en = 'Must be at least 2 characters';
  }

  return errors;
};

function SessionTypeCreateDialog({ open, onClose }) {
  const dispatch = useDispatch();
  const { t } = useTranslation(['buttons', 'labels']);
  const status = useSelector((state) => state.sessionType.status);

  useEffect(() => {
    // Resetear el estado cuando se abre o se cierra el modal
    dispatch(sessionTypeSlice.actions.resetStatus());
  }, [open, dispatch]);

  useEffect(() => {
    // Cerrar el diálogo cuando se crea exitosamente
    if (status === requestStatus.success && open) {
      onClose();
    }
  }, [status, open, onClose]);

  const onCreate = useCallback(
    (values) => {
      const dataToSave = {
        name: values.name_es,
        name_en: values.name_en,
        price: 1,
        currency: 'COP'
      };
      dispatch(sessionTypeSlice.actions.create(dataToSave));
    },
    [dispatch]
  );

  const handleClose = () => {
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {t('add', { ns: 'buttons' })}
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      {status === requestStatus.loading ? (
        <DialogContent>
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
            <CircularProgress />
          </Box>
        </DialogContent>
      ) : (
        <Form
          initialValues={{ name_es: '', name_en: '' }}
          onSubmit={onCreate}
          validate={validate}
          render={({ handleSubmit, invalid, pristine }) => (
            <form onSubmit={handleSubmit}>
              <DialogContent>
                <Grid container spacing={3}>
                  <Grid xs={12}>
                    <Field
                      id="name_es"
                      name="name_es"
                      component={TextFieldAdapter}
                      label="Nombre (Español)"
                      type="text"
                      required
                      fullWidth
                      inputProps={{ minLength: 2 }}
                    />
                  </Grid>
                  <Grid xs={12}>
                    <Field
                      id="name_en"
                      name="name_en"
                      component={TextFieldAdapter}
                      label="Name (English)"
                      type="text"
                      required
                      fullWidth
                      inputProps={{ minLength: 2 }}
                    />
                  </Grid>
                </Grid>
              </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
              <StyledAppbarButtonLink
                onClick={handleClose}
                disabled={status === requestStatus.loading}
                component="button"
                type="button"
                size="small"
                variant="outlined"
                sx={{
                  background: 'transparent',
                  border: '1px solid',
                  borderColor: 'grey.400',
                  color: 'text.primary',
                  '&::before': { display: 'none' },
                  '&:hover': {
                    background: 'grey.100',
                    borderColor: 'grey.600'
                  }
                }}
              >
                <span>{t('cancel', { ns: 'buttons' })}</span>
              </StyledAppbarButtonLink>
              <PrimaryLoadingButton
                disabled={invalid || pristine || status === requestStatus.loading}
                loading={status === requestStatus.loading}
                label={t('add', { ns: 'buttons' })}
                type="submit"
              />
            </DialogActions>
          </form>
        )}
      />
      )}
    </Dialog>
  );
}

SessionTypeCreateDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired
};

export default SessionTypeCreateDialog;
